/**
 * 首页
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-use';
import { observer } from 'mobx-react-lite';
import { Button, message } from 'antd';
import ChatInput from '@/components/chat/ChatInput';
import Footer from '@/components/footer';
import globalStore from '@/store/global';
import modelChoiceStore from '@/components/model-choice/store';
import store from './store';
import request from '@/common/requestSafe';
import { ErrCodes } from '@/common/request-esc/errcodes';
import { usePageDropzone } from '@/components/page-dropzone';
import chatStoreManager from '@/store/chat/manager';
import ScrollView from '@/ui/scroll-view';
import InputHeader from './components/input-header';
import BusinessSwiper from './components/business-swiper';
import PersonalAgent from './components/personal-agent';
import FeaturedAgent from './components/featured-agent';
import MoreAgent from './components/more-agent';
import { fetchAgentList } from '@/services/agentService';
import kbStore from '@/components/kb-node-tree/kb-store';
import userStore from '@/store/user';
import { getLoginInfo, getLoginInfoByForceRefresh } from '@/common/login-info';
import './index.scss';
import agentPageStore from '../agent-home/store';
import { copyTextToClipboard } from './utils'

/**
 * 大模型 首页
 */
const Home = observer(() => {
  const location = useLocation();
  const sugList = store.sugList;
  // Agent数据状态
  const [agentData, setAgentData] = useState({
    bannerImg: '',
    featuredAgents: [],
    moreAgents: [],
    agentCategories: [],
    loading: true,
    error: null,
  });

  const isBizUserLogin = useMemo(() => {
    return !!(userStore.bizLoginInfo && userStore.isUser);
  }, [userStore.bizLoginInfo, userStore.isUser]);

  let assistantHomeBanner = globalStore.cloudConfig?.assistantHomeBanner || [];
  assistantHomeBanner = assistantHomeBanner[0] || {};

  const handleClickOutside = event => {
    try {
      if (event.target.className.includes('ant-input')) {
        return;
      }
    } catch (error) {}
    if (store.curSugInfo.category) {
      store.setData('curSugInfo', {});
    }
  };

  const checkLoginStatus = useCallback(() => {
    request.get(`${import.meta.env.VITE_SAFE_HOST}/api/agent/uc/usage/hot`).then(res => {
      if (res.errno === ErrCodes.loginExpired) {
        message.error('QT过期');
      } else if (res.errno === ErrCodes.tokenInvalid) {
        message.error('token过期');
      } else {
        message.success('登录态正常');
      }
    });
  }, []);

  const copyLoginInfo = useCallback(() => {
    getLoginInfo().then(loginInfo => {
      copyTextToClipboard(JSON.stringify(loginInfo?.data ?? {}, undefined, 2), () => {
        message.success('复制成功');
      });
    });
  }, []);

  const copyLoginInfoByForceRefresh = useCallback(() => {
    getLoginInfoByForceRefresh().then(loginInfo => {
      copyTextToClipboard(JSON.stringify(loginInfo?.data ?? {}, undefined, 2), () => {
        message.success('复制成功');
      });
    });
  }, [])

  // 获取Agent数据
  const fetchAgentData = async () => {
    try {
      setAgentData(prev => ({ ...prev, loading: true, error: null }));
      const data = await fetchAgentList();
      setAgentData({
        bannerImg: data.bannerImg,
        featuredAgents: data.featuredAgents.sort((a, b) => a.order - b.order),
        moreAgents: data.moreAgents.sort((a, b) => a.order - b.order),
        agentCategories: data.agentCategories,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('获取Agent数据失败:', error);
      setAgentData(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    if (!isBizUserLogin) {
      return;
    }
    agentPageStore.getAgents();
    chatStoreManager.setCurrentId(null);
    document.addEventListener('click', handleClickOutside);
    fetchAgentData();
    const urlObj = new URL(window.location.href);
    kbStore.fetchTreeData({
      kbId: urlObj.searchParams.get('kbId'),
      containChildren: urlObj.searchParams.get('containChildren'),
    });
    return () => {
      document.removeEventListener('click', handleClickOutside);
      agentPageStore.switchTab(0);
    };
  }, [isBizUserLogin]);

  // 通过判断是否存在 <UploadButton id=Tag_UploadButton /> 组件来决定是否激活拖放上传
  const [activeDropzone, setActiveDropzone] = useState(false);
  useEffect(() => {
    setTimeout(() => {
      const isUploadBtn = document.getElementById('Tag_UploadButton');
      if (isUploadBtn) {
        return setActiveDropzone(true);
      }
      setActiveDropzone(false);
    }, 500);
  }, [location]);
  const { getRootProps, HideInput, DragArea, isDragActive } = usePageDropzone({
    currAsstId: modelChoiceStore.curId,
  });

  return (
    <div
      className="w-full h-full flex flex-col relative"
      {...getRootProps(activeDropzone)}
      style={{
        backgroundImage: `url(https://p2.ssl.qhimg.com/t110b9a93013be09a129a3803c2.png)`,
        backgroundSize: '20px 20px',
      }}
    >
      {isDragActive ? <DragArea active={activeDropzone} /> : null}
      <HideInput active={activeDropzone} />
      <ScrollView className="home-main-content-wrap outline-none h-0 flex-grow pt-100px pb-80px">
        <div
          id="input_content"
          className={[
            'w-full flex flex-col items-center relative px-40px',
            'transform transition duration-500 z-10',
          ]}
        >
          <div className="w-full h-296px absolute left-0 z-0 dynamic-top">
            <div
              className="size-full"
              style={{
                backgroundImage: `url(${agentData.bannerImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div
              style={{
                background:
                  'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 67%, #ffffff 100%)',
              }}
              className="h-120px absolute bottom-0 w-full"
            ></div>
          </div>
          <InputHeader showModel={isBizUserLogin} logo={isBizUserLogin ? userStore.bizLoginInfo.userInfo.company_logo : ''} />
          <div className="home-input-wrap w-full max-w-800px min-w-652px z-2 relative">
            <ChatInput
              classname={['border-none rounded-tl-none rounded-tr-none']}
              scene="home"
              disabled={!isBizUserLogin}
              sugStore={store}
              showToolbars={isBizUserLogin}
              sug={{ ...store.curSugInfo, ...(sugList[store.curSugInfo.category] || {}) }}
            />
          </div>
          {isBizUserLogin && (
            <>
              <BusinessSwiper userInfo={userStore.bizLoginInfo.userInfo} />
              <PersonalAgent />
              <FeaturedAgent
                featuredAgents={agentData.featuredAgents}
                loading={agentData.loading}
                error={agentData.error}
              />
              <MoreAgent
                moreAgents={agentData.moreAgents}
                agentCategories={agentData.agentCategories}
                loading={agentData.loading}
                error={agentData.error}
              />
            </>
          )}
        </div>
        <div className="absolute w-full h-full z-1 left-0 top-0 flex flex-col pointer-events-none">
          <div
            style={{
              height: 'calc(100vh - 500px)',
            }}
          ></div>
          <div
            className="bg-black h-0 flex-grow w-full"
            style={{
              background: `linear-gradient(
                to bottom,
                rgba(255,255,255,0) 0,
                rgba(255,255,255,1) 500px
              )`,
            }}
          ></div>
        </div>
      </ScrollView>
      <Footer className="pointer-events-none w-full h-80px mx-auto bg-primary-2" />
      {import.meta.env.VITE_MODE !== 'online' && import.meta.env.VITE_MODE !== 'master' && (
        <div className="fixed bottom-[24px] right-[24px] z-1000">
          <Button
            type="primary"
            onClick={checkLoginStatus}
            style={{ display: 'block', marginBottom: '12px' }}
          >
            检查登录态（通过/hot接口）
          </Button>
          <Button type="primary" onClick={copyLoginInfo} style={{ display: 'block', marginBottom: '12px' }}>
            复制登录信息
          </Button>
          <Button type="primary" onClick={copyLoginInfoByForceRefresh}>
            复制登录信息 (强制刷新)
          </Button>
        </div>
      )}
    </div>
  );
});

export default Home;
